import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import {
  useGetEmailNotificationsQuery,
  useGetEmailNotificationStatsQuery,
} from '@/apis/extended/admin/emailNotificationApi';
import { CommonDataGrid } from '@/components/Common/CommonDataGrid';
import { Loader } from '@/components/Layout/Loader';

export const EmailNotificationDashboard = () => {
  const [filters, setFilters] = useState({
    status: '',
    emailTemplateKey: '',
    startDate: '',
    endDate: '',
  });

  const { data: stats, isLoading: statsLoading } =
    useGetEmailNotificationStatsQuery({
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
    });

  const { data: notifications, isLoading: notificationsLoading } =
    useGetEmailNotificationsQuery({
      limit: 50,
      offset: 0,
      ...filters,
    });

  const columns: GridColDef[] = [
    {
      field: 'emailAddress',
      headerName: 'Recipient',
      width: 200,
    },
    {
      field: 'emailTemplateKey',
      headerName: 'Template',
      width: 200,
    },
    {
      field: 'subject',
      headerName: 'Subject',
      width: 300,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const statusColors: Record<string, string> = {
          sent: '#1976d2',
          delivered: '#2e7d32',
          opened: '#ed6c02',
          clicked: '#9c27b0',
          failed: '#d32f2f',
        };
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              backgroundColor: statusColors[params.value] || '#666',
              color: 'white',
            }}
          />
        );
      },
    },
    {
      field: 'sentAt',
      headerName: 'Sent At',
      width: 180,
      renderCell: (params) => {
        if (!params.value) return '-';
        return new Date(params.value).toLocaleString();
      },
    },
    {
      field: 'openedAt',
      headerName: 'Opened At',
      width: 180,
      renderCell: (params) => {
        if (!params.value) return '-';
        return new Date(params.value).toLocaleString();
      },
    },
    {
      field: 'clickedAt',
      headerName: 'Clicked At',
      width: 180,
      renderCell: (params) => {
        if (!params.value) return '-';
        return new Date(params.value).toLocaleString();
      },
    },
  ];

  if (statsLoading || notificationsLoading) {
    return <Loader isFull />;
  }

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 3 }}>
        Email Notification Dashboard
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Sent
              </Typography>
              <Typography variant="h4">{stats?.totalSent || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Open Rate
              </Typography>
              <Typography variant="h4">{stats?.openRate || '0'}%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Click Rate
              </Typography>
              <Typography variant="h4">{stats?.clickRate || '0'}%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Failed
              </Typography>
              <Typography variant="h4" color="error">
                {stats?.failed || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value })
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="opened">Opened</MenuItem>
                  <MenuItem value="clicked">Clicked</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Template Key"
                value={filters.emailTemplateKey}
                onChange={(e) =>
                  setFilters({ ...filters, emailTemplateKey: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={filters.startDate}
                onChange={(e) =>
                  setFilters({ ...filters, startDate: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={filters.endDate}
                onChange={(e) =>
                  setFilters({ ...filters, endDate: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Notifications Table */}
      <Card>
        <CardContent>
          <CommonDataGrid
            columns={columns}
            rows={notifications?.notifications || []}
            rowCount={notifications?.count || 0}
            paginationModel={{ page: 0, pageSize: 50 }}
            loading={notificationsLoading}
          />
        </CardContent>
      </Card>
    </Box>
  );
};
